import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Row, Col, Button, Space, Spin, Empty, Typography, Table } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { emotionApi } from '@tradermind/shared/api';
import { getEmotionById } from '@tradermind/shared/constants';

const { Title, Text } = Typography;

export default function EmotionAnalysisPage() {
  const { t } = useTranslation('emotion');
  const navigate = useNavigate();

  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['emotions', 'stats'],
    queryFn: () => emotionApi.getEmotionStats(),
  });

  const { data: correlationData, isLoading: isLoadingCorrelation } = useQuery({
    queryKey: ['emotions', 'performance-correlation'],
    queryFn: () => emotionApi.getEmotionPerformanceCorrelation(),
  });

  const stats = statsData?.data;
  const correlations = correlationData?.data || [];

  const distributionData = stats?.emotion_distribution
    ? Object.entries(stats.emotion_distribution).map(([emotion, count]) => {
        const emotionInfo = getEmotionById(emotion as any);
        return {
          name: emotionInfo?.label ? t(emotionInfo.label) : emotion,
          value: count,
          color: emotionInfo?.color || '#999',
        };
      })
    : [];

  const correlationColumns = [
    {
      title: t('analysis.columns.emotion'),
      dataIndex: 'emotion_type',
      key: 'emotion_type',
      render: (emotion: string) => {
        const info = getEmotionById(emotion as any);
        return (
          <Space>
            <span>{info?.emoji}</span>
            <span>{info?.label ? t(info.label) : emotion}</span>
          </Space>
        );
      },
    },
    {
      title: t('analysis.columns.trade_count'),
      dataIndex: 'trade_count',
      key: 'trade_count',
    },
    {
      title: t('analysis.columns.win_rate'),
      dataIndex: 'win_rate',
      key: 'win_rate',
      render: (rate: number) => (
        <span style={{ color: rate >= 50 ? '#10B981' : '#EF4444' }}>{rate.toFixed(1)}%</span>
      ),
    },
    {
      title: t('analysis.columns.avg_pl'),
      dataIndex: 'average_profit_loss',
      key: 'average_profit_loss',
      render: (pl: number) => (
        <span style={{ color: pl >= 0 ? '#10B981' : '#EF4444' }}>
          {pl >= 0 ? '+' : ''}${pl.toFixed(2)}
        </span>
      ),
    },
    {
      title: t('analysis.columns.recommendation'),
      dataIndex: 'recommendation',
      key: 'recommendation',
      render: (rec: string) => <Text type="secondary">{rec}</Text>,
    },
  ];

  if (isLoadingStats || isLoadingCorrelation) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/emotions')}>
            {t('back')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t('analysis.title')}
          </Title>
        </Space>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title={t('analysis.distribution')}>
            {distributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={distributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {distributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={t('analysis.no_data')} />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('analysis.intensity_trend')}>
            {stats?.intensity_trend && stats.intensity_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.intensity_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 10]} />
                  <Tooltip />
                  <Bar dataKey="average_intensity" fill="#3B82F6" name={t('analysis.avg_intensity')} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={t('analysis.no_data')} />
            )}
          </Card>
        </Col>

        <Col xs={24}>
          <Card title={t('analysis.performance_correlation')}>
            <Table
              columns={correlationColumns}
              dataSource={correlations}
              rowKey="emotion_type"
              pagination={false}
              locale={{ emptyText: <Empty description={t('analysis.no_data')} /> }}
            />

            {correlations.length > 0 && (
              <div style={{ marginTop: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
                <Title level={5}>{t('analysis.key_insights')}</Title>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {correlations
                    .filter((c) => c.win_rate >= 60)
                    .map((c) => (
                      <li key={c.emotion_type}>
                        {t('analysis.insight_high_win', { emotion: t(getEmotionById(c.emotion_type)?.label || ''), rate: c.win_rate.toFixed(1) })}
                      </li>
                    ))}
                  {correlations
                    .filter((c) => c.win_rate < 40)
                    .map((c) => (
                      <li key={c.emotion_type} style={{ color: '#EF4444' }}>
                        {t('analysis.insight_low_win', { emotion: t(getEmotionById(c.emotion_type)?.label || ''), rate: c.win_rate.toFixed(1) })}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
